import { useCallback, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import GuidedTour from "../UI/GuidedTour";
import classes from "../UI/GuidedTour.module.css";

// Tour state is local. Opening/closing never dispatches settings or touches audio.
export default function EarTrainerQuickTour() {
  const training = useSelector(state => !!state.earTrainerMode);
  const [active, setActive] = useState(false);
  const scroll = useRef(null);
  const close = useCallback(() => {
    setActive(false);
    if (scroll.current?.element.isConnected) {
      scroll.current.element.scrollTop = scroll.current.top;
      scroll.current.element.scrollLeft = scroll.current.left;
      window.scrollTo({ top: scroll.current.y, left: scroll.current.x, behavior: "instant" });
    }
  }, []);
  const steps = useMemo(() => [
    {
      target: "ear-notes", title: "Choose your instrument and scale",
      text: "Choose your instrument, scale and Practice diagram. Together they determine the notes and positions used in your listening exercise. Your selections stay unchanged during this tour.",
    },
    {
      target: "ear-options", title: "Adjust the challenge",
      text: "Choose your musical root and how much help you want. Show scale dots and interval-name hints to begin, then hide them for more listening practice. These are separate settings.",
    },
    {
      target: "ear-start", title: "Start listening",
      text: training
        ? "Training is already active. Replay the root or question whenever you need to hear it again. Stop Training returns to exploration; this tour leaves the exercise running."
        : "Start Training plays a root note followed by a question note. The root provides your musical reference. During training, replay either note whenever you need to hear it again.",
    },
    {
      target: "ear-fretboard", title: "Find the note you hear",
      getTarget: () => document.querySelector('[data-tour-target="ear-fretboard"] img'),
      text: training
        ? "Select the pitch you heard anywhere on the fretboard, including unmarked positions. Green means correct and advances automatically; red means try again. You can answer after closing the tour."
        : "Here, exploration clicks audition notes. During training, select the pitch you heard anywhere, including unmarked positions. Green advances automatically; red lets you try again.",
    },
  ].map(step => ({ ...step, unavailableText: "This part of the exercise is currently unavailable. Continue or exit the tour; your settings have not been changed." })), [training]);

  return <>
    <button type="button" className={classes.start} onClick={event => {
      const element = event.currentTarget.closest('[data-ear-trainer-page]');
      scroll.current = element ? { element, top: element.scrollTop, left: element.scrollLeft,
        x: window.scrollX, y: window.scrollY } : null;
      setActive(true);
    }}>Take a quick tour</button>
    {active && <GuidedTour steps={steps} onClose={close} />}
  </>;
}
